// @givmo/app-charity — the direct-link gate around the Donations route family (CPA-WEB40 · contract
// §Mounting mechanic 3 · shell contract v3, T5.1).
//
// Arriving at `/donations/*` by address bar, bookmark or mail needs the SAME answer the tile gives,
// rendered as a page: the package's `ModuleGate` turns the console's ONE standing read into it —
// `locked` for a Member without the grant BEFORE any statement read fires (the gate replaces the
// children), with the SAME request door the tile carries; `unavailable` with a retry after a
// transport failure; nothing at all while the read settles.
//
// It never renders `absent` or `off` for Donations: the class's own module is always on and never
// switchable, and a REPORTED map that lacks the row has already been read as held+on by the ONE
// §9-14 rule in `charity-console-context.tsx` before this gate sees it.

import type { ReactNode } from 'react';
import { ModuleGate, type ModuleReadState } from '@givmo/org-console';
import { NotFound } from '../components/not-found';
import { useRequestAccessDoor } from '../components/request-access-context';
import { useCharityConsole } from './charity-console-context';
import { CLASS_DEFAULT_SLUG, charityModuleDefinition } from './module-table';
import { useModuleNavigate } from './use-console-squares';
import { useTurnOnHref } from './use-turn-on-href';

export const DONATIONS_SLUG = CLASS_DEFAULT_SLUG;

/** The gate's read for Donations — the console's one standing read, as the package reads it. */
export function useDonationsRead(): ModuleReadState {
  const { pending, errored, standings } = useCharityConsole();
  return { pending, errored, standings };
}

export function DonationsModuleGate({ children }: { children: ReactNode }) {
  const { standings, refetch } = useCharityConsole();
  const read = useDonationsRead();
  const door = useRequestAccessDoor();
  const goInApp = useModuleNavigate();
  const turnOnHref = useTurnOnHref(DONATIONS_SLUG);
  const standing = standings?.[DONATIONS_SLUG];
  const accessRequest =
    standing?.accessRequest?.status === 'PENDING'
      ? { status: 'pending' as const }
      : standing?.accessRequest?.status === 'DENIED'
        ? { status: 'declined' as const, note: standing.accessRequest.resolutionNote ?? null }
        : undefined;
  return (
    <ModuleGate
      slug={DONATIONS_SLUG}
      read={read}
      label={charityModuleDefinition(DONATIONS_SLUG)?.label ?? 'Donations'}
      renderNotFound={() => <NotFound />}
      onRetry={refetch}
      turnOnHref={turnOnHref}
      onNavigate={goInApp}
      // The request door is withheld on a module the person already holds (409 for a holder).
      onRequestAccess={standing?.held === true ? undefined : () => door.open(DONATIONS_SLUG)}
      accessRequest={accessRequest}
    >
      {children}
    </ModuleGate>
  );
}
